/**
 * Personalización de la propuesta comercial. Dos piezas:
 *  - `radiografiaDeCatastro`: arma las celdas de la radiografía (pág. 2) desde
 *    los datos de catastro + lo que el comercial cargue.
 *  - `personalizarHeuristico`: genera los párrafos "cómo te resuelve tu edificio"
 *    (pág. 3) combinando catastro + servicios + perfil de propietarios + el
 *    contexto libre que describe el comercial. Es el FALLBACK cuando la IA
 *    (Habi) no está disponible; la IA devuelve la MISMA forma { coverLead,
 *    precioLead, secciones }, así el resto del flujo no cambia.
 *
 * Regla de tono (igual que el resto del material): cercano, concreto, sin
 * prometer nada que no esté acordado.
 */

export const PERFILES_PROPIETARIOS = [
  { value: "mixto", label: "Mixto (residentes + veraneo)" },
  { value: "veraneo", label: "Mayormente no residentes / veraneo" },
  { value: "residentes", label: "Mayormente residentes" },
];

const fmtN = (n) => (n == null || n === "" ? null : Number(n).toLocaleString("es-UY"));

/** Celdas de la radiografía (hasta 4) desde catastro + carga manual. */
export function radiografiaDeCatastro({ unidades, anio, plantas, zona, regimen, m2, extraLabel } = {}) {
  const cells = [];
  if (unidades) cells.push({ n: String(unidades), l: "Unidades" });
  if (plantas) cells.push({ n: String(plantas), l: "Plantas" });
  if (anio) cells.push({ n: String(anio), l: "Construcción" });
  if (m2) cells.push({ n: `${fmtN(m2)}`, l: "m² edificados" });
  if (zona) cells.push({ n: zona, l: extraLabel || "Zona" });
  if (regimen) cells.push({ n: regimen, l: "Régimen" });
  return cells.slice(0, 4);
}

const anioAntiguo = (anio) => anio && (new Date().getFullYear() - Number(anio)) > 30;

/**
 * Genera { coverLead, precioLead, secciones[] } con heurística.
 * @param {object} p
 * @param {string} p.edificio
 * @param {string} p.zona
 * @param {"mixto"|"veraneo"|"residentes"} p.perfil
 * @param {object} p.servicios  { portero, amenities, garages, local }
 * @param {number} p.anio
 * @param {string} p.contexto   texto libre del comercial
 */
export function personalizarHeuristico({ edificio = "el edificio", zona = "", perfil = "mixto", servicios = {}, anio, contexto = "" } = {}) {
  const veraneo = perfil === "veraneo";
  const antiguo = anioAntiguo(anio);
  const ctx = (contexto || "").trim();
  const ctxFrase = ctx ? ` Además, nos comentaste: ${ctx.replace(/\s+/g, " ").replace(/\.$/, "")}.` : "";

  const secciones = [];

  // 1 · Transparencia
  secciones.push({
    titulo: "Transparencia total de gastos",
    cuerpo: veraneo
      ? `Con propietarios que casi no están en ${zona || "la zona"}, la rendición nunca se ve de cerca. Con Habitia cada uno abre la app —desde donde esté— y ve su estado de cuenta, cada gasto con su factura y cómo se reparte. Se terminan las dudas de “¿por qué pago esto si no estuve?”.`
      : `Cada propietario ve en la app su estado de cuenta y cada gasto del edificio con su factura y cómo se reparte. Se terminan las dudas de “¿por qué pago esto?” y las decisiones se toman con los números a la vista.`,
  });

  // 2 · Habi / WhatsApp
  secciones.push({
    titulo: "Habi y todo por WhatsApp",
    cuerpo: veraneo
      ? `Con la gente repartida entre distintas ciudades, juntar a todos es imposible. Por eso todo se hace por WhatsApp —consultar la deuda, avisar un problema, enterarse de una novedad— sin instalar nada. Y Habi, nuestro asistente con IA, responde al instante, a cualquier hora.`
      : `Todo se puede hacer también por WhatsApp —consultar la deuda, avisar un problema, ver una novedad— sin instalar nada. Y Habi, el asistente con IA, responde al instante a cualquier hora. Nadie queda afuera, maneje apps o no.`,
  });

  // 3 · Incidencias (antigüedad + contexto libre)
  secciones.push({
    titulo: "Incidencias con foto y seguimiento",
    cuerpo: (antiguo
      ? `Un edificio con años tiene lo suyo: humedades, el ascensor, instalaciones que piden mantenimiento. `
      : `El mantenimiento del día a día no espera. `)
      + `Cada problema se reporta con una foto desde el celular y se le sigue el estado —con el proveedor coordinado— hasta que se resuelve. Nada queda en una cadena de llamados.` + ctxFrase,
  });

  // 4 · Cobranza
  secciones.push({
    titulo: "Cobranza y cuenta a la vista",
    cuerpo: veraneo
      ? `Recordatorios automáticos antes de cada vencimiento y pago online desde cualquier lado, sin depender de ir a un local. La cuenta bancaria del edificio queda a la vista con conciliación al día: la morosidad de veraneo es lo que más complica, y así se mantiene baja.`
      : `Recordatorios automáticos antes de cada vencimiento y la cuenta bancaria del edificio en vivo, con conciliación al día. Mantener la morosidad baja es lo que permite afrontar un arreglo cuando aparece.`,
  });

  // 5 · Personal + amenities (según servicios)
  const tienePortero = servicios.portero;
  const tieneAmen = servicios.amenities;
  if (tienePortero || tieneAmen) {
    const partes = [];
    if (tienePortero) partes.push("liquidamos el sueldo del portero/conserje —BPS, aguinaldo, licencias—");
    if (tieneAmen) partes.push("coordinamos los amenities y sus reservas desde la app");
    secciones.push({
      titulo: "Portero, amenities, sueldos y asambleas",
      cuerpo: `${partes.join(" y ").replace(/^\w/, (m) => m.toUpperCase())}. Y las asambleas se preparan con el estado del edificio resumido y el acta redactada: ustedes conducen, nosotros el papeleo, y quien no puede ir participa igual.`,
    });
  } else {
    secciones.push({
      titulo: "Sueldos, asambleas y actas",
      cuerpo: `Si el edificio tiene personal, liquidamos sueldos, BPS, aguinaldo y licencias. Y las asambleas se preparan con el estado del edificio resumido y el acta redactada: ustedes conducen la reunión, nosotros el papeleo.`,
    });
  }

  // 6 · Auditoría IA
  secciones.push({
    titulo: "Auditoría de gastos con IA",
    cuerpo: `Revisamos los contratos del edificio —seguro, ascensor, limpieza${tieneAmen ? ", mantenimiento de amenities" : ""}— y detectamos dónde se puede ahorrar. Comparar proveedores y ordenar los gastos suele ser la diferencia en la cuota.`,
  });

  const coverLead = `Propuesta preparada para ${edificio}${zona ? `, en ${zona}` : ""}. No es una presentación genérica: cada punto está pensado para lo que vive hoy este edificio.`;
  const precioLead = `Esta propuesta contempla la administración integral de ${edificio}. Precios en pesos uruguayos IVA incluido.`;

  return { coverLead, precioLead, secciones };
}

/** Payload que consume la IA (Habi) — misma info que la heurística. */
export function payloadPersonalizacion({ edificio, ciudad, zona, perfil, servicios, anio, unidades, m2, regimen, contexto }) {
  return { edificio, ciudad, zona, perfil, servicios, anio, unidades, m2, regimen, contexto };
}
